import API from '../utils/api';

export const PUSH_SETUP_OK_KEY = 'push-setup-complete-v2';

const isPushRuntime = () => process.env.NODE_ENV === 'production';

export function getNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

export function isStandalonePwa() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

/** Subscribe if permission is already granted. Safe to call on app load. */
export async function ensurePushSubscription() {
  if (!isPushRuntime()) {
    console.info('[Push] Disabled outside production');
    return null;
  }
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return null;
  }
  return subscribeToPush();
}

/**
 * Must be called from a user tap (iOS PWA will ignore permission
 * requests from timers / useEffect).
 */
export const requestPushPermission = async () => {
  if (!isPushRuntime()) {
    console.info('[Push] Disabled outside production');
    return null;
  }

  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return null;
  }

  if (Notification.permission === 'granted') {
    return await subscribeToPush();
  }

  if (Notification.permission === 'denied') {
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission === 'granted') {
    return await subscribeToPush();
  }

  return null;
};

export const subscribeToPush = async () => {
  if (!process.env.REACT_APP_VAPID_PUBLIC_KEY) {
    console.warn('[Push] Missing VAPID public key — skipping subscription');
    const err = new Error('missing_vapid_public_key');
    err.code = 'missing_vapid_public_key';
    throw err;
  }

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('Service Worker / PushManager not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        process.env.REACT_APP_VAPID_PUBLIC_KEY || ''
      ),
    });

    const payload = {
      ...(subscription.toJSON
        ? subscription.toJSON()
        : {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: arrayBufferToBase64Url(subscription.getKey('p256dh')),
              auth: arrayBufferToBase64Url(subscription.getKey('auth')),
            },
          }),
      userAgent: navigator.userAgent,
    };

    await API.post('/pushnotifications/subscribe/', payload);

    return subscription;
  } catch (error) {
    if (error?.code === 'missing_vapid_public_key') throw error;
    console.error('Error subscribing to push:', error);
    return null;
  }
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const arrayBufferToBase64Url = (buffer) => {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
